import { z } from "zod";
/**
 * **`RoomSummary` — architecture §16f A45, the canonical room half.**
 *
 * Eight facts about a room, and **nothing about any player**. Every one of them
 * decides what a screen says or what a control does (A45's own test; a field
 * that only decides how something *looks* belongs in the client, per A43).
 *
 * ── Where the numbers come from — LOCKED (§5.4) ─────────────────────────────
 *
 * `entryFeeCoins`, `prizeCoins` and the phase/reason pair are read from
 * `rooms.*_snapshot` and `rooms.phase` / `rooms.cancel_reason` — **never from
 * live `room_configs`.** A nullable column arrives as `null`, never as `""`
 * (T-217): the absence of a cancel reason is a fact two routing rules read. A player being told what happened to their entry must
 * be told the number they were actually charged, not the one an admin has since
 * edited into config. A reuse in S5 must not "refresh" them.
 *
 * The two display names come from config because they are labels rather than
 * promises, and `""` is a survivable answer: a room whose config row has gone
 * still gets its terminal panel.
 *
 * ── The split, and it is the whole reason this shape is named ───────────────
 *
 * > **`seatOutcome` — and anything else about the viewer — stays a sibling
 * > field, never a member of `RoomSummary`** (A45, LOCKED). A summary that
 * > quietly contains one viewer's personal outcome is safe exactly until it is
 * > reused for a second audience, at which point it leaks — and it leaks
 * > *silently*, because the shape "worked".
 *
 * The addendum to A45 puts `me.prizeCoins` on the same side of that line: it is
 * a sibling of `seatOutcome`, never a member here. Note that this shape's own
 * `prizeCoins` is the **room's** prize — what the room promised in total — and
 * the viewer's credited amount is a different number with the same word in it.
 * They are on opposite sides of the split precisely so they cannot be confused,
 * and nesting is what makes that structural rather than a naming convention.
 *
 * `RoomSummary` carries **no player list and no other player's name, seat or
 * outcome.** If a future view needs those, that is a new decision about what one
 * player may learn about another, and it goes back to the architecture.
 *
 * First consumer: the refused-rejoin body (`rejoinFailureDetailsSchema`).
 * T-208's 409 `seat_closed` body already names `roomId` and **may adopt this
 * shape the next time it changes** — A45 is explicit that it is not worth a
 * refactor today, and T-215's ticket repeats it: do not churn a body Quinn is
 * about to test.
 */
export const roomSummarySchema = z.object({
    /** **`rooms.id`** — the comparison space (A41), what the spent-room set records. */
    roomId: z.string(),
    /** `room_configs.id`. What `Join again` / `Play again` re-join: the config. */
    roomConfigId: z.string(),
    /** `game_types.name`, for the `{type}` in `room.heading`. `""` if config is gone. */
    gameTypeName: z.string(),
    /** `game_modes.name`, the `{mode}`. */
    gameModeName: z.string(),
    /** `rooms.entry_fee_snapshot`. */
    entryFeeCoins: z.number().int().min(0),
    /**
     * `rooms.prize_snapshot` — **the room's** prize, in total. Not anybody's
     * credit: at prize 0 the room is free and T8 renders no money line at all.
     */
    prizeCoins: z.number().int().min(0),
    /** `rooms.phase` as persisted: `filling` | `ready_check` | `in_progress` | `completed` | `cancelled`. */
    phase: z.string(),
    /**
     * `rooms.cancel_reason`, or **`null` — the column's own absence** (T-217,
     * Quinn N3). It was `""` until then, which is a third state nobody specced:
     * §19 line 23 routes *"every other or absent reason = T1"* and A45's shape
     * keys on the reason being **there or not**, so the wire has to carry that
     * distinction rather than a sentinel the reader has to know about.
     *
     * `all_disconnected` is the one value T7 may read; every other value, and
     * `null`, is T1.
     */
    cancelReason: z.string().nullable(),
});
//# sourceMappingURL=room-summary.js.map